/**
 * AdvertisementSeoMeta.js
 *
 * SEO + structured data for the Free Consultation / Advertisement landing
 * page (route: /freeConsultation).
 *
 * Mirrors the exact pattern of ContactSeoMeta.js:
 *   - Keyed <Helmet> so React always re-renders on canonical change
 *   - trimDesc() respects word boundaries (155 char cap)
 *   - BreadcrumbList JSON-LD
 *   - Service JSON-LD
 *   - Open Graph + Twitter Card
 *
 * NOTE ON INDEXING: this page is typically reached from paid ads rather
 * than organic search, and its content overlaps heavily with /packages
 * and /contact. To avoid it competing with those pages for the same
 * keywords, it's set to noindex,follow below — Google won't index it,
 * but will still follow/credit any links on it. Remove the <meta
 * name="robots" .../> line if you do want this page to rank organically.
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME      = "Yoga Shastra";
const SITE_URL       = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image.jpg`;
const TWITTER_HANDLE = "@yogashastra";
const PHONE          = "+917838866331";

// ─── Helpers ───────────────────────────────────────────────────────────────────
// Mirrors ContactSeoMeta / BlogSeoMeta — word-boundary aware trim
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// AdvertisementMeta — used on /freeConsultation
// ═══════════════════════════════════════════════════════════════════════════════

export const AdvertisementMeta = () => {
  const canonical   = `${SITE_URL}/freeConsultation`;
  const title       = `Free Yoga Consultation | ${SITE_NAME}`;
  const description = trimDesc(
    "Book a free consultation with a Yoga Shastra Yogacharya and get " +
    "personalised guidance on the right prenatal, postnatal, or wellness " +
    "yoga plan for you."
  );
  const keywords =
    "free yoga consultation, yoga shastra offer, book free demo, " +
    "prenatal yoga consultation";

  // ── BreadcrumbList ───────────────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",               item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Free Consultation",  item: canonical },
    ],
  };

  // ── Service ───────────────────────────────────────────────────────────────────
  const serviceSchema = {
    "@context":  "https://schema.org",
    "@type":     "Service",
    name:         "Free Yoga Consultation",
    description,
    url:          canonical,
    provider: {
      "@type":     "Organization",
      name:         SITE_NAME,
      url:          SITE_URL,
      logo:        { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
      telephone:    PHONE,
      contactPoint: {
        "@type":        "ContactPoint",
        telephone:       PHONE,
        contactType:    "customer support",
        availableLanguage: ["English", "Hindi"],
        areaServed:     "IN",
      },
    },
    areaServed: "IN",
  };

  // Keyed on canonical — mirrors ContactSeoMeta / BlogSeoMeta
  return (
    <Helmet key={canonical}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords"    content={keywords} />
      <link rel="canonical"    href={canonical} />

      {/* Keep this page out of organic search results — see note above */}
      <meta name="robots" content="noindex,follow" />

      {/* Open Graph */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
    </Helmet>
  );
};